import { supabase } from '@/integrations/supabase/client'
import type { AccessCode, AppState } from './types'

export { supabase }

// ─────────────────────────────────────────────
// DÉTECTION MODE
// ─────────────────────────────────────────────
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string ?? ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string ?? ''

const isSupabaseReady =
  typeof supabaseUrl === 'string' &&
  supabaseUrl.length > 0 &&
  !supabaseUrl.includes('placeholder') &&
  typeof supabaseAnonKey === 'string' &&
  supabaseAnonKey.length > 0 &&
  !supabaseAnonKey.includes('placeholder')

export const storageMode: 'supabase' | 'local' = isSupabaseReady ? 'supabase' : 'local'

// ─────────────────────────────────────────────
// CLÉS LOCALSTORAGE
// ─────────────────────────────────────────────
const LS_CODES = 'lhc_access_codes_v2'
const LS_LEADS = 'lhc_demo_leads_v2'

// ─────────────────────────────────────────────
// HELPERS LOCALSTORAGE
// ─────────────────────────────────────────────
function readCodes(): AccessCode[] {
  try {
    const raw = localStorage.getItem(LS_CODES)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeCodes(codes: AccessCode[]): void {
  try {
    localStorage.setItem(LS_CODES, JSON.stringify(codes))
  } catch {
    // quota dépassé – ignorer
  }
}

function readLeads(): Record<string, unknown>[] {
  try {
    const raw = localStorage.getItem(LS_LEADS)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeLeads(leads: Record<string, unknown>[]): void {
  try {
    localStorage.setItem(LS_LEADS, JSON.stringify(leads))
  } catch {
    // quota dépassé – ignorer
  }
}

// ─────────────────────────────────────────────
// HASH MOT DE PASSE
// ─────────────────────────────────────────────
export async function hashPassword(password: string): Promise<string> {
  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(password)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    // Fallback si SubtleCrypto indisponible (http sans HTTPS)
    return password.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0).toString(16)
  }
}

// ─────────────────────────────────────────────
// CRÉER UN CODE D'ACCÈS
// ─────────────────────────────────────────────
export async function createAccessCode(prospect: {
  code: string
  prenom: string
  nom: string
  salon: string
  email: string
  telephone?: string
  adresse?: string
}): Promise<{ success: boolean; error?: string }> {

  // Toujours stocker le code en MAJUSCULES
  const normalizedProspect = { ...prospect, code: prospect.code.toUpperCase() }

  if (!isSupabaseReady) {
    // ── MODE LOCAL ──
    try {
      const codes = readCodes()
      const exists = codes.some((c) => c.code === normalizedProspect.code)
      if (exists) return { success: false, error: 'Ce code existe déjà. Générez-en un nouveau.' }

      const newEntry: AccessCode = {
        id: `local_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        code: normalizedProspect.code,
        prenom: prospect.prenom,
        nom: prospect.nom,
        salon: prospect.salon,
        email: prospect.email,
        telephone: prospect.telephone ?? '',
        adresse: prospect.adresse ?? '',
        password_hash: undefined,
        created_at: new Date().toISOString(),
        first_login_at: undefined,
        last_login_at: undefined,
        is_active: true,
      }

      writeCodes([newEntry, ...codes])
      return { success: true }
    } catch (err) {
      return { success: false, error: `Erreur locale : ${String(err)}` }
    }
  }

  // ── MODE SUPABASE ──
  try {
    const { error } = await supabase
      .from('access_codes')
      .insert([{ ...normalizedProspect, is_active: true }])
    if (error) return { success: false, error: error.message }
    return { success: true }
  } catch (err) {
    return { success: false, error: `Erreur réseau : ${String(err)}` }
  }
}

// ─────────────────────────────────────────────
// LISTER LES CODES
// ─────────────────────────────────────────────
export async function listAccessCodes(): Promise<{ data: AccessCode[]; error: unknown }> {
  if (!isSupabaseReady) {
    return { data: readCodes(), error: null }
  }

  try {
    const { data, error } = await supabase
      .from('access_codes')
      .select('*')
      .order('created_at', { ascending: false })
    return { data: (data ?? []) as AccessCode[], error }
  } catch (err) {
    return { data: [], error: err }
  }
}

// ─────────────────────────────────────────────
// VÉRIFIER UN CODE D'ACCÈS (connexion prospect)
// ─────────────────────────────────────────────
export async function verifyAccessCode(
  code: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  const upperCode = code.trim().toUpperCase()

  if (!upperCode.startsWith('SALON-') || upperCode.length < 8) {
    return { success: false, error: 'Format de code invalide. Exemple : SALON-Ab3Xyz9K' }
  }

  const passwordHash = await hashPassword(password)

  if (!isSupabaseReady) {
    // ── MODE LOCAL ──
    const codes = readCodes()
    const entry = codes.find(
      (c) => c.code.toUpperCase() === upperCode && c.is_active === true
    )

    if (!entry) {
      return { success: false, error: 'Code d\'accès introuvable. Vérifiez le code reçu.' }
    }

    const now = new Date().toISOString()

    if (!entry.password_hash) {
      // Première connexion : enregistrer le mot de passe
      const updated = codes.map((c) =>
        c.code.toUpperCase() === upperCode
          ? { ...c, password_hash: passwordHash, first_login_at: now, last_login_at: now }
          : c
      )
      writeCodes(updated)
      return { success: true }
    }

    if (entry.password_hash !== passwordHash) {
      return { success: false, error: 'Mot de passe incorrect.' }
    }

    // Mettre à jour last_login
    const updated = codes.map((c) =>
      c.code.toUpperCase() === upperCode ? { ...c, last_login_at: now } : c
    )
    writeCodes(updated)
    return { success: true }
  }

  // ── MODE SUPABASE ──
  try {
    const { data, error } = await supabase
      .from('access_codes')
      .select('*')
      .eq('code', upperCode)
      .eq('is_active', true)
      .single()

    if (error || !data) {
      return { success: false, error: 'Code d\'accès invalide ou inactif.' }
    }

    if (!data.password_hash) {
      const now = new Date().toISOString()
      await supabase
        .from('access_codes')
        .update({ password_hash: passwordHash, first_login_at: now, last_login_at: now })
        .eq('code', upperCode)
      return { success: true }
    }

    if (data.password_hash !== passwordHash) {
      return { success: false, error: 'Mot de passe incorrect.' }
    }

    await supabase
      .from('access_codes')
      .update({ last_login_at: new Date().toISOString() })
      .eq('code', upperCode)

    return { success: true }
  } catch (err) {
    return { success: false, error: `Erreur réseau : ${String(err)}` }
  }
}

// ─────────────────────────────────────────────
// CHANGER LE MOT DE PASSE (utilisateur connecté)
// ─────────────────────────────────────────────
export async function changePassword(
  code: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  if (newPassword.length < 8) {
    return { success: false, error: 'Le nouveau mot de passe doit contenir au moins 8 caractères.' }
  }

  const upperCode = code.trim().toUpperCase()
  const currentHash = await hashPassword(currentPassword)
  const newHash = await hashPassword(newPassword)

  if (!isSupabaseReady) {
    const codes = readCodes()
    const entry = codes.find(c => c.code.toUpperCase() === upperCode)
    if (!entry) return { success: false, error: 'Compte introuvable.' }
    if (entry.password_hash && entry.password_hash !== currentHash) {
      return { success: false, error: 'Mot de passe actuel incorrect.' }
    }
    const updated = codes.map(c =>
      c.code.toUpperCase() === upperCode ? { ...c, password_hash: newHash } : c
    )
    writeCodes(updated)
    return { success: true }
  }

  try {
    const { data, error } = await supabase
      .from('access_codes')
      .select('password_hash')
      .eq('code', upperCode)
      .single()

    if (error || !data) return { success: false, error: 'Compte introuvable.' }
    if (data.password_hash && data.password_hash !== currentHash) {
      return { success: false, error: 'Mot de passe actuel incorrect.' }
    }

    const { error: updateError } = await supabase
      .from('access_codes')
      .update({ password_hash: newHash })
      .eq('code', upperCode)

    if (updateError) return { success: false, error: updateError.message }
    return { success: true }
  } catch (err) {
    return { success: false, error: `Erreur réseau : ${String(err)}` }
  }
}

// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
export async function saveLead(lead: {
  prenom: string
  nom: string
  email: string
  telephone: string
  salon: string
  access_code?: string
}): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseReady) {
    const leads = readLeads()
    leads.unshift({
      ...lead,
      id: `lead_${Date.now()}`,
      created_at: new Date().toISOString(),
    })
    writeLeads(leads)
    return { success: true }
  }

  try {
    const { error } = await supabase.from('demo_leads').insert([lead])
    if (error) return { success: false, error: error.message }
    return { success: true }
  } catch (err) {
    return { success: false, error: String(err) }
  }
}

// ─────────────────────────────────────────────
// LISTER LES LEADS (admin)
// ─────────────────────────────────────────────
export async function listLeads(): Promise<{ data: Record<string, unknown>[]; error: unknown }> {
  if (!isSupabaseReady) {
    return { data: readLeads(), error: null }
  }

  try {
    const { data, error } = await supabase
      .from('demo_leads')
      .select('*')
      .order('created_at', { ascending: false })
    return { data: (data ?? []) as Record<string, unknown>[], error }
  } catch (err) {
    return { data: [], error: err }
  }
}

// ─────────────────────────────────────────────
// ADMIN — SUPPRIMER UN CODE
// ─────────────────────────────────────────────
export async function deleteAccessCode(id: string): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseReady) {
    const codes = readCodes().filter(c => c.id !== id)
    writeCodes(codes)
    return { success: true }
  }
  try {
    const { error } = await supabase.from('access_codes').delete().eq('id', id)
    if (error) return { success: false, error: error.message }
    return { success: true }
  } catch (err) { return { success: false, error: String(err) } }
}

// ─────────────────────────────────────────────
// ADMIN — MODIFIER LE CODE D'UN PROSPECT
// ─────────────────────────────────────────────
export async function updateProspectCode(
  id: string,
  newCode: string,
  updates?: { prenom?: string; nom?: string; salon?: string; email?: string; telephone?: string }
): Promise<{ success: boolean; error?: string }> {
  const upperCode = newCode.toUpperCase()
  if (!isSupabaseReady) {
    const codes = readCodes()
    const exists = codes.some(c => c.code === upperCode && c.id !== id)
    if (exists) return { success: false, error: 'Ce code existe déjà.' }
    const updated = codes.map(c => c.id === id
      ? { ...c, code: upperCode, password_hash: undefined, first_login_at: undefined, ...updates }
      : c)
    writeCodes(updated)
    return { success: true }
  }
  try {
    const { error } = await supabase
      .from('access_codes')
      .update({ code: upperCode, password_hash: null, first_login_at: null, ...updates })
      .eq('id', id)
    if (error) return { success: false, error: error.message }
    return { success: true }
  } catch (err) { return { success: false, error: String(err) } }
}

// ─────────────────────────────────────────────
// DONNÉES SALON (isolation par code d'accès)
// ─────────────────────────────────────────────

// Charger les données d'un salon depuis Supabase
export async function loadSalonData(code: string): Promise<AppState | null> {
  if (!isSupabaseReady) return null
  try {
    const { data, error } = await supabase
      .from('salon_data')
      .select('data')
      .eq('access_code', code.toUpperCase())
      .single()
    if (error || !data) return null
    return data.data as AppState
  } catch { return null }
}

// Sauvegarder les données d'un salon dans Supabase
export async function saveSalonData(code: string, state: AppState): Promise<void> {
  if (!isSupabaseReady) return
  try {
    await supabase.from('salon_data').upsert({
      access_code: code.toUpperCase(),
      data: state,
      updated_at: new Date().toISOString()
    }, { onConflict: 'access_code' })
  } catch { /* silencieux */ }
}
